/** Shared list-polling helpers: keep a resource list fresh while anything in it
 *  is mid-transition (creating/deleting/pending/…), then stop. */
import type { QueryClient } from "@tanstack/react-query";

// 5s keeps creates/attaches feeling live while halving the request volume of
// long transitions (a k8s cluster build polls for 10+ minutes). Polling only
// runs while something is transitional — see the per-hook refetchInterval.
export const LIST_POLL_MS = 5000;

/**
 * Heuristic for "this OpenStack-ish resource is mid-transition, keep polling".
 * Covers Cinder (creating/attaching/reserved), Nova (BUILD), Glance
 * (queued/saving/importing), Octavia (PENDING_*), Heat/Magnum (*_IN_PROGRESS),
 * Trove (BUILD/REBOOT), Designate (PENDING). Terminal error states return false
 * so we don't poll forever on a failure.
 */
export function isTransitionalStatus(
  status: string | null | undefined,
): boolean {
  if (!status) return false;
  const s = status.toUpperCase();
  if (s.includes("ERROR") || s.includes("FAIL")) return false;
  return (
    s.includes("ING") || // creating, deleting, building, attaching, restoring…
    s.includes("PENDING") || // octavia PENDING_CREATE/UPDATE/DELETE
    s.includes("IN_PROGRESS") || // heat / magnum *_IN_PROGRESS
    ["BUILD", "BUILDING", "SCHEDULING", "RESERVED", "QUEUED", "SAVING", "IMPORTING"].includes(
      s,
    )
  );
}

/** When, after an action went through, what it changed is read again. */
export const MUTATION_FOLLOW_UP_MS: readonly number[] = [2_000, 5_000, 12_000, 30_000];

/**
 * Read a list again, a few times, after an action that changed it (B6,
 * platform test round 4: a new function, cluster template, load balancer or
 * auto scaling group "did not appear until Refresh", and a cluster "kept
 * showing Ready after deletion started").
 *
 * Every one of those actions already invalidated the right list — once, at
 * the moment the API answered. But the cloud answers a create or a delete
 * when it has *accepted* it: the item is not in the listing yet (or still
 * reads Ready), so that one re-read came back unchanged, and a list polls
 * only while it holds a row in transition — which the row it has not got yet
 * cannot be. Nothing read it again.
 *
 * So the queries an action invalidated are invalidated again on a short
 * schedule. Only those (never every query on the page — see
 * `OperationsTracker`), only the ones on screen are fetched, and a burst of
 * actions (a bulk delete) shares one schedule. Once the new row is listed in
 * a transitional state the list's own `refetchInterval` takes over.
 *
 * Mount once, beside the QueryClient. Returns the function that stops it.
 */
export function followUpAfterMutations(
  queryClient: QueryClient,
  delays: readonly number[] = MUTATION_FOLLOW_UP_MS,
): () => void {
  /** How close to an action's success an invalidation counts as its own. */
  const OWN_MS = 1_000;
  const touched = new Set<string>();
  const invalidatedAt = new Map<string, number>();
  let armedUntil = 0;
  let following = false;
  let timers: ReturnType<typeof setTimeout>[] = [];
  const clear = () => {
    for (const timer of timers) clearTimeout(timer);
    timers = [];
  };
  const schedule = () => {
    clear();
    timers = delays.map((delay, round) =>
      setTimeout(() => {
        following = true;
        try {
          void queryClient.invalidateQueries(
            { predicate: (query) => touched.has(query.queryHash), refetchType: "active" },
            // A read already on its way is the read we wanted.
            { cancelRefetch: false },
          );
        } finally {
          following = false;
        }
        if (round === delays.length - 1) {
          touched.clear();
          timers = [];
        }
      }, delay),
    );
  };
  // What an action invalidates, it invalidates in its success handler: just
  // before the cache reports the success, or (after an awaited `mutateAsync`)
  // just after. Both are taken; this function's own re-reads are not.
  const stopQueries = queryClient.getQueryCache().subscribe((event) => {
    if (following || event.type !== "updated" || event.action.type !== "invalidate") return;
    const now = Date.now();
    if (now <= armedUntil) {
      touched.add(event.query.queryHash);
      schedule();
      return;
    }
    invalidatedAt.set(event.query.queryHash, now);
  });
  const stopMutations = queryClient.getMutationCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "success") return;
    const now = Date.now();
    armedUntil = now + OWN_MS;
    let added = false;
    for (const [hash, at] of invalidatedAt) {
      if (now - at <= OWN_MS) {
        touched.add(hash);
        added = true;
      }
    }
    invalidatedAt.clear();
    if (added) schedule();
  });
  return () => {
    stopQueries();
    stopMutations();
    clear();
    touched.clear();
    invalidatedAt.clear();
  };
}
